import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCloud } from '../context/CloudContext';
import Icon from './Icon';
import { ICONS } from '../lib/icons';

// Static navigable destinations (mirrors the sidebar). Resources and Activity
// are hidden from navigation, so they are not offered here either.
const PAGES = [
  { label: 'Dashboard', to: '/', icon: ICONS.dashboard },
  { label: 'Providers', to: '/providers', icon: ICONS.providers },
  { label: 'Billing', to: '/billing', icon: ICONS.billing },
  { label: 'Cost & Billing', to: '/costs', icon: ICONS.costs },
  { label: 'Price management', to: '/prices', icon: ICONS.prices },
  { label: 'Superadmin', to: '/superadmin', icon: ICONS.superadmin },
];

// AWS-style global search: a prominent input with a keyboard-navigable
// results dropdown covering pages and connected providers. Press "/" or
// Option/Alt+S anywhere to focus it.
export default function SearchBar() {
  const navigate = useNavigate();
  const { providers } = useCloud();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const blurTimer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
      if ((e.key === '/' && !typing) || (e.altKey && (e.key === 's' || e.key === 'S'))) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pages = PAGES.filter((p) => !q || p.label.toLowerCase().includes(q)).map(
      (p) => ({ ...p, kind: 'Page' })
    );
    const provs = providers
      .filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          (p.shortName || '').toLowerCase().includes(q)
      )
      .map((p) => ({
        label: p.name,
        to: `/providers/${p.id}`,
        icon: ICONS.providers,
        kind: 'Provider',
      }));
    return [...pages, ...provs];
  }, [query, providers]);

  useEffect(() => setActive(0), [query]);

  const select = (item) => {
    if (!item) return;
    navigate(item.to);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  let lastKind = null;

  return (
    <div className="appbar__search">
      <Icon name={ICONS.search} className="appbar__search-icon" />
      <input
        ref={inputRef}
        className="appbar__search-input"
        type="text"
        placeholder="Search resources, services, and pages"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          clearTimeout(blurTimer.current);
          setOpen(true);
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={onKeyDown}
        aria-label="Search"
      />
      <span className="appbar__search-kbd">[Option+S]</span>

      {open && (
        <div className="search-results" role="listbox">
          {results.length === 0 ? (
            <div className="search-results__empty">No matches for “{query}”.</div>
          ) : (
            results.map((item, i) => {
              const header =
                item.kind !== lastKind ? (
                  <div className="search-group__title">
                    {item.kind === 'Page' ? 'Pages' : 'Providers'}
                  </div>
                ) : null;
              lastKind = item.kind;
              return (
                <div key={`${item.kind}-${item.to}-${i}`}>
                  {header}
                  <div
                    className={`search-item ${i === active ? 'search-item--active' : ''}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      select(item);
                    }}
                  >
                    <Icon name={item.icon} className="search-item__icon" />
                    <span className="search-item__label">{item.label}</span>
                    <span className="search-item__kind">{item.kind}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
